import { returnToCreationTab } from '../new/creation-tab-context';
import type { NavigationScreen } from 'app/types/navigation';
import { withErrorBoundary } from 'app/components/error-boundary';
import { useBlockDatesForListings, useManyListing } from 'app/network/listing';
import { Column, Row } from 'app/design/primitives/box';
import { Calendar } from 'app/design/primitives/calendar';
import { eachDayOfInterval, isBefore, isSameDay, isWithinInterval, startOfDay, subDays } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useUser } from 'app/network/user';
import { Text } from 'app/design/primitives/text';
import { Button } from 'app/design/primitives/button';
import { Page } from 'app/design/primitives/page';

import { localeAwareFormat } from 'app/lib/date';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigationOptions } from 'app/hooks/navigation';
import { Accessory } from 'app/design/primitives/page/accessory';
import { ListingThumbnail } from 'app/components/listing-thumbnail';
import { TopLoadingIndicator } from 'app/components/top-loading-indicator';
import { Separator } from 'app/design/primitives/text-divider';
import { useDistanceUnit } from 'app/hooks/use-distance-unit';
import { StorageKey } from 'app/lib/storage';
import { useOfferAvailabilityPopup } from 'app/screens/management/refrain-offer-availability-popup';
import { View } from 'react-native';
import type { AcceptedLoan, Listing } from 'app/types/listing';
import { ListingType } from 'app/types/listing';
import type { User } from 'app/types/user';
import { usePageTitle } from 'app/utils/web-helpers';
import { sum } from 'app/utils/helpers';
import { useOpenNewWithGuard } from 'app/hooks/use-open-new';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconAlertTriangle, IconInfoCircle } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import type { TFunction } from 'i18next';
const theme = createDSTheme();

type AcceptedLoanWithListing = AcceptedLoan & {
  listing: Omit<Listing, 'loans'>;
};

interface ConflictGroup {
  dateString: string;
  dates: Date[];
  key: string;
  loans: AcceptedLoanWithListing[];
}

function buildLoans(
  listings: Listing[] | undefined,
  listingsFetched: boolean,
  isDonationDate: boolean,
): AcceptedLoanWithListing[] {
  if (!listingsFetched || (isDonationDate && Boolean(listings?.[0]?.before))) {
    return [];
  }

  return (
    listings?.flatMap((listing) => {
      const { loans, ...listingWithoutLoans } = listing;
      return (
        loans?.map(
          (loan) =>
            ({
              ...loan,
              listing: listingWithoutLoans as AcceptedLoanWithListing['listing'],
            }) satisfies AcceptedLoanWithListing,
        ) ?? []
      );
    }) ?? []
  );
}

function getLoansByOwner(loans: AcceptedLoanWithListing[], mine: boolean, userId?: User['id']) {
  return loans.filter(({ borrower }) => (mine ? borrower?.id === userId : borrower?.id !== userId));
}

function getDatesFromLoans(loans: AcceptedLoanWithListing[], commonDatesOnly: boolean, numLoansNeeded: number) {
  const allDatesInclDuplicates = loans
    .flatMap(({ start, end }) => eachDayOfInterval({ start: new Date(start), end: new Date(end) }))
    .sort((a, b) => a.getTime() - b.getTime());

  if (!commonDatesOnly) {
    return allDatesInclDuplicates.filter((date, index) => allDatesInclDuplicates.indexOf(date) === index);
  }

  const numLoansPerDate: Record<string, number> = {};
  allDatesInclDuplicates.forEach((date) => {
    const key = date.getTime().toString();
    if (!numLoansPerDate[key]) {
      numLoansPerDate[key] = 0;
    }
    numLoansPerDate[key]++;
  });

  return Object.entries(numLoansPerDate).flatMap(([timestamp, numLoans]) =>
    numLoans === numLoansNeeded ? [new Date(Number(timestamp))] : [],
  );
}

function getUnavailableDates({
  blockedDateParams,
  isDonationDate,
  isNewListing,
  listings,
  loans,
  mine,
  singleDate,
  userId,
}: {
  blockedDateParams?: string[];
  isDonationDate: boolean;
  isNewListing: boolean;
  listings?: Listing[];
  loans: AcceptedLoanWithListing[];
  mine: boolean;
  singleDate: boolean;
  userId?: User['id'];
}) {
  let dates: Date[];
  if (mine && isDonationDate && listings?.length && listings[0]?.before) {
    dates = [new Date(listings[0].before)];
  } else if (mine && isNewListing && blockedDateParams?.length) {
    dates = blockedDateParams.map((date) => new Date(date));
  } else {
    dates = getDatesFromLoans(getLoansByOwner(loans, mine, userId), mine, listings?.length ?? 1);
  }

  return singleDate ? dates.slice(0, 1) : dates;
}

function formatConflictDates(dates: Date[], t: TFunction<'offerAvailability'>) {
  const datesByMonth: Record<string, Date[]> = {};
  dates.forEach((date) => {
    const month = localeAwareFormat(date, 'MMMM');
    datesByMonth[month] ??= [];
    datesByMonth[month].push(date);
  });

  return Object.values(datesByMonth)
    .map((monthDates) => {
      const dateGroups: { from: Date; to: Date }[] = [];
      monthDates.forEach((date, index) => {
        const previousDate = monthDates[index - 1];
        if (previousDate && isSameDay(subDays(date, 1), previousDate)) {
          dateGroups[dateGroups.length - 1]!.to = date;
        } else {
          dateGroups.push({ from: date, to: date });
        }
      });

      const daysString = dateGroups
        .map(({ from, to }) =>
          isSameDay(from, to)
            ? localeAwareFormat(from, 'd')
            : `${localeAwareFormat(from, 'd')}-${localeAwareFormat(to, 'd')}`,
        )
        .join(',');
      const monthDate = dateGroups[0]!.from;
      const template = localeAwareFormat(monthDate, t('dateFormat_MMMMd', { ns: 'common' }));
      return template.replace(localeAwareFormat(monthDate, 'd'), daysString);
    })
    .join('; ');
}

function buildConflicts(
  selectedDates: Date[],
  unavailableDates: Date[],
  otherLoans: AcceptedLoanWithListing[],
  t: TFunction<'offerAvailability'>,
): ConflictGroup[] {
  const conflictsByLoan: Record<string, Omit<ConflictGroup, 'dateString'>> = {};

  selectedDates.forEach((date) => {
    if (!unavailableDates.find((unavailableDate) => isSameDay(unavailableDate, date))) return;

    const loans = otherLoans.filter(({ start, end }) =>
      isWithinInterval(date, { start: new Date(start), end: new Date(end) }),
    );
    const key = loans.reduce((acc, loan) => `${acc}${loan.listing.id}${loan.borrower?.id ?? ''}`, '');
    conflictsByLoan[key] ??= { key, dates: [], loans: [] };
    conflictsByLoan[key].dates.push(date);
    conflictsByLoan[key].loans = loans;
  });

  return Object.values(conflictsByLoan).map((conflict) => ({
    ...conflict,
    dateString: formatConflictDates(conflict.dates, t),
  }));
}

function getScreenTitle({
  isDonationDate,
  isSellDate,
  listingCount,
  t,
}: {
  isDonationDate: boolean;
  isSellDate: boolean;
  listingCount: number;
  t: TFunction<'offerAvailability'>;
}) {
  if (isSellDate) return t('titleSell');
  if (isDonationDate) return t('titleDonation');
  return listingCount <= 1 ? t('title') : t('titleMany', { count: listingCount });
}

const OfferAvailabilityScreenComponent: NavigationScreen<'offer-availability'> = ({ navigation, route }) => {
  const { formatDistance } = useDistanceUnit();
  const { t } = useTranslation('offerAvailability');
  const { data: user } = useUser();
  const listingIds = route.params.listingId?.split(',') ?? [];
  const isNewListing = listingIds.length === 0;
  const { data: listings, isFetchedAfterMount: listingsFetched } = useManyListing(listingIds, 'offer-availability');
  const { mutate: saveBlockedDates, isPending: isSaving } = useBlockDatesForListings(listingIds);
  const singleDate = Boolean(route.params.singleDate);
  const isSellDate = singleDate && route.params.singleDateType === 'sell';
  const isDonationDate = singleDate && route.params.singleDateType !== 'sell';
  const today = startOfDay(new Date());

  const loans = buildLoans(listings, listingsFetched, isDonationDate);

  const isMyListing = listings?.[0]?.userId === user?.id;

  // Blocked dates are loans I made for my own offers
  const blockedDates = getUnavailableDates({
    blockedDateParams: route.params.blockedDates,
    isDonationDate,
    isNewListing,
    listings,
    loans,
    mine: true,
    singleDate,
    userId: user?.id,
  });
  // This is other users' reservations (accepted loans)
  const unavailableDates = getUnavailableDates({
    blockedDateParams: route.params.blockedDates,
    isDonationDate,
    isNewListing,
    listings,
    loans,
    mine: false,
    singleDate,
    userId: user?.id,
  });

  // Follow the API value until the user makes their first selection.
  const [selectedDatesOverride, setSelectedDatesOverride] = useState<Date[]>();
  const selectedDates = selectedDatesOverride ?? blockedDates;

  const conflicts = buildConflicts(selectedDates, unavailableDates, getLoansByOwner(loans, false, user?.id), t);

  // Toggle dates on or off when pressed
  const addSelectedDate = (date: Date) =>
    singleDate
      ? setSelectedDatesOverride([date])
      : setSelectedDatesOverride([...selectedDates, date].sort((a, b) => (isBefore(a, b) ? -1 : 1)));

  const removeSelectedDate = (date: Date) => {
    const newSelectedDates = selectedDates.filter((d) => !isSameDay(d, date));
    const wasRemoved = newSelectedDates.length !== selectedDates.length;
    if (wasRemoved) {
      setSelectedDatesOverride(newSelectedDates);
    }
    return wasRemoved;
  };

  const onDatePress = (date: Date) => {
    if (isDonationDate && isBefore(date, today)) return;

    if (!removeSelectedDate(date)) {
      addSelectedDate(date);
    }
  };

  const openNewWithGuard = useOpenNewWithGuard();
  const saveDates = () => {
    if (isNewListing && route.params.returnToPublish) {
      returnToCreationTab(navigation, { blockedDates: selectedDates.map((d) => d.toISOString()) });
      return;
    }
    if (isNewListing) {
      // navigation.popTo()
      openNewWithGuard(navigation, {
        blockedDates: selectedDates.map((d) => d.toISOString()),
      });
      return;
    }
    saveBlockedDates(selectedDates, {
      onSuccess: () =>
        route.params.goBack
          ? navigation.goBack()
          : navigation.navigate(
              'main',
              {
                screen: 'management',
                params: {
                  screen: 'my-listings',
                  params: { type: ListingType.offer, isSelecting: false },
                },
              },
              { pop: true },
            ),
    });
  };

  const isDirty = sum(selectedDates.map((date) => date.getTime())) !== sum(blockedDates.map((date) => date.getTime()));

  const title = getScreenTitle({ isDonationDate, isSellDate, listingCount: listings?.length ?? 0, t });

  useNavigationOptions(navigation, {
    headerBackVisible: false,
    headerTitle: () => (
      <Text variant="bodyXLargeMedium" color="white-label.header.on-header">
        {title}
      </Text>
    ),
  });
  const setPageTitle = usePageTitle();
  useEffect(() => setPageTitle(title), [setPageTitle, title]);

  const showPopupMaybe = useOfferAvailabilityPopup(
    StorageKey.RefrainBlockedDatesPopupWasShownLenderBefore,
    navigation,
    { isSaleOrDonation: isSellDate || isDonationDate },
  );
  showPopupMaybe();

  // When using a pan gesture to select dates, we need to disable the scroll view temporarily
  const [scrollEnabled, setScrollEnabled] = useState(true);

  return (
    <Page
      accessory={
        isDirty ? (
          <Accessory style={{ marginHorizontal: theme.spacing['spacing.general.x4'] }}>
            <Button loading={isSaving} onPress={saveDates} variant="primary">
              {t('saveButton')}
            </Button>
          </Accessory>
        ) : undefined
      }
      isModal
      scrollEnabled={scrollEnabled}
    >
      <Column gap="spacing.general.x4">
        {!isSellDate ? (
          <Row backgroundColor="surface.tertiary-neutral" borderRadius="radius.lg" padding="spacing.general.x4">
            <Row flex={1}>
              <IconInfoCircle strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['surface.tertiary']} />
            </Row>

            <Text flex={7} fontSize={14} fontWeight="400">
              {isDonationDate ? t('informationTextDonation') : t('informationText')}
            </Text>
          </Row>
        ) : null}

        <Calendar
          {...{ onDatePress }}
          canSelectUnavailable
          daysSelectionMode="individual"
          disableParentScroll={(disabled) => setScrollEnabled(!disabled)}
          minDate={isNewListing || singleDate ? today : undefined}
          selected={selectedDates}
          startFromNow={singleDate}
          unavailableDates={unavailableDates}
        />

        <View style={{ overflow: 'hidden' }}>
          <TopLoadingIndicator isLoading={!listingsFetched} position="relative" />
        </View>

        {conflicts.length > 0 && !isMyListing ? (
          <Column
            gap="spacing.general.x3"
            style={{
              backgroundColor: theme.colors['surface.brand.secondary-4.subtle'],
              paddingHorizontal: theme.spacing['spacing.general.x6'],
              paddingVertical: theme.spacing['spacing.general.x4'],
              borderRadius: theme.borderRadii['radius.lg'],
            }}
          >
            <Row gap="spacing.general.x4">
              <IconAlertTriangle strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['icon.error']} />
              <Text alignSelf="center" color="icon.error" fontSize={16} fontWeight="bold" variant="bodyBase">
                {t('conflictsTitle')}
              </Text>
            </Row>

            <Text variant="bodyBase">
              <Trans
                components={{ strong: <Text fontWeight="600" /> }}
                i18nKey="conflictsInformationText"
                ns="offerAvailability"
              />
            </Text>

            {conflicts.map((conflict) => (
              <Column gap="spacing.general.x1" key={conflict.key} marginTop="spacing.general.x1">
                <Separator color={theme.colors['surface.brand.secondary-4.default']} />

                <Text color="icon.error" marginVertical="spacing.general.x2" variant="bodyBase">
                  <Trans
                    components={{ strong: <Text color="icon.error" fontWeight="600" /> }}
                    i18nKey="conflictWhen"
                    ns="offerAvailability"
                    tOptions={{ dates: conflict.dateString }}
                  />
                </Text>

                <Column gap="spacing.general.x2">
                  {conflict.loans.map((loan) => (
                    <Row alignItems="center" gap="spacing.general.x4" key={loan.listing.id + loan.borrower!.id}>
                      <ListingThumbnail
                        {...{
                          categoryPrimary: loan.listing.categoryPrimary,
                          picture: loan.listing.picture,
                          type: ListingType.offer,
                        }}
                        isFound={!!loan.listing.lender}
                        size={66}
                      />

                      <Column gap="spacing.general.x2">
                        <Text fontWeight="bold" variant="bodyBase">
                          {loan.listing.item.name}
                        </Text>
                        <Column gap="spacing.general.x0,5">
                          <Text variant="bodyBase">
                            {`${loan.borrower!.profile!.firstName} ${loan.borrower!.profile!.lastName}`}
                          </Text>
                          {loan.distance !== undefined ? (
                            <Text fontWeight="bold" variant="bodyBase">
                              {formatDistance(loan.distance)}
                            </Text>
                          ) : null}
                        </Column>
                      </Column>
                    </Row>
                  ))}
                </Column>
              </Column>
            ))}
          </Column>
        ) : null}
      </Column>
    </Page>
  );
};

export const OfferAvailabilityScreen: NavigationScreen<'offer-availability'> = withErrorBoundary(
  OfferAvailabilityScreenComponent,
);
